/**
 * This is the container component for the AssessmentOutcomesWidget written in React functional component style in TypeScript.
 * This container passes the necessary props to the AssessmentOutcomesWidget.
 * It will use useSelector and useDispatch hooks from react-redux to interact with the Redux store.
 * It will also use the AssessmentOutcomesWidgetModel to apply business logic to the
 * data as needed before passing it to the AssessmentOutcomesWidget.
 */
import React, { useEffect, useMemo } from 'react';
import { WidgetWrapper } from './WidgetWrapper';
import { WidgetCategory, WidgetType, AsyncLoadingState } from '../enums';
import { useSelector, useDispatch } from 'react-redux';
import {
  selectAssessmentOutcomesWidget,
  fetchAssessmentOutcomesWidgetData,
  fetchAssessmentDyslexiaRiskWidgetData,
} from "../store/slices/assessmentOutcomesWidgetSlice";
import { AssessmentOutcomesView } from "@/enums";
import {
  dashboardWidgetState,
  toggleWidget,
  WidgetStateEnum,
} from '@/store/slices';
import AssessmentOutcomesWidget from './AssessmentOutcomesWidget';
import { useTranslation } from 'react-i18next';
import assessmentOutcomesIcon from "@/images/icons/brain.png";
import useSelectedClassroom from '@/hooks/useSelectedClassroom';
import { useAssessmentPeriodWindows } from '@/hooks/useAssessmentPeriods';

export const AssessmentOutcomesContainer: React.FC = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation('dashboard');

  // Fetch the AssessmentOutcomesWidget data from the Redux store
  const { loading, error, view } = useSelector(
    selectAssessmentOutcomesWidget
  );
  const { assessment } = useSelector(dashboardWidgetState);
  const { classroomId } = useSelectedClassroom();
  const assessmentCalendar = useAssessmentPeriodWindows();

  // Dispatch the fetchAssessmentOutcomesWidgetData action to fetch the data from the API
  // Wait for calendar data to be loaded before fetching widget data
  useEffect(() => {
    if (assessment.outcome === WidgetStateEnum.COLLAPSED) return;
    if(!classroomId) return;
    if (assessmentCalendar.loading) return; // Wait for calendar data to load

    if(view === AssessmentOutcomesView.DYSLEXIA_RISK){
      dispatch(fetchAssessmentDyslexiaRiskWidgetData(classroomId) as any);
    }else{
      dispatch(fetchAssessmentOutcomesWidgetData(classroomId) as any);
    }
  }, [dispatch, assessment, classroomId, view, assessmentCalendar.loading]);

  // Combine calendar loading state with widget loading state
  const combinedLoadingState = useMemo(() => {
    if (assessmentCalendar.loading) {
      return AsyncLoadingState.LOADING;
    }
    return loading;
  }, [assessmentCalendar.loading, loading]);

  return (
    <WidgetWrapper
      title="Assessment Outcomes"
      loadingState={combinedLoadingState}
      type={WidgetType.ASSESSMENT_OUTCOMES}
      category={WidgetCategory.ASSESS}
      icon={assessmentOutcomesIcon}
      isCollapsed={assessment.outcome === WidgetStateEnum.COLLAPSED}
      onCollapserHandler={() =>
        dispatch(toggleWidget({ group: 'assessment', widget: 'outcome' }))
      }
      id="assessment-outcomes"
      dataTestId="assessment-outcomes-widget"
      overriddenTitle={`${t(`WIDGET_TITLE_${view}`)}`}>
      <AssessmentOutcomesWidget />
    </WidgetWrapper>
  );
};

export default AssessmentOutcomesContainer;
